import React from "react";
import type { Localizer } from "@/main/react/Types";
import type { ThoughtVersionView } from "@/main/common/ThoughtEditTypes";
import { SimpleDialog, openSimpleDialog } from "@/main/react/Dialog/SimpleDialog";
import DateFormatter from "@/main/common/DateFormatter";

export type ThoughtEditHistoryDialogOptions = {
    localizer: Localizer;
    versions: ThoughtVersionView[];
};

/**
 * The OWNER's edit history for one reply (editable replies, design §3.6, slice 2b): every stored version,
 * newest first, each with its number and time. Owner only — the card offers it from the reply's own menu,
 * and the endpoint behind it answers the author and platform moderators and nobody else (design §8.2);
 * moderators read versions from the report instead.
 *
 * A `SimpleDialog` at `width="md"`, per the design: one readable column, which is what a list of short
 * replies is. Flat-X: dividers, not cards; no shadow; semantic tokens only. The HTML is the
 * server-sanitized, link-rel-stamped copy, rendered the way the card renders the live reply.
 */
export function showThoughtEditHistoryDialog({ localizer, versions }: ThoughtEditHistoryDialogOptions): () => void {
    const formatter = new DateFormatter(localizer);
    const newestFirst = [...versions].sort((a, b) => b.versionNumber - a.versionNumber);
    return openSimpleDialog(({ open, onOpenChange }) => (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={localizer("thought.history.title")}
            width="md"
            dataPlaywrightId="thought-edit-history-dialog"
        >
            <ol className="divide-y divide-border" data-playwright-id="thought-edit-history-list">
                {newestFirst.map((version) => (
                    <li
                        key={version.versionNumber}
                        className="py-4 first:pt-0 last:pb-0"
                        data-playwright-id="thought-edit-history-version"
                        data-version-number={version.versionNumber}
                    >
                        <div className="flex flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground">
                            <span className="font-medium text-foreground">
                                {localizer("thought.history.version", version.versionNumber)}
                            </span>
                            <time dateTime={version.createdAt} title={formatter.formatDateTime(version.createdAt)}>
                                {formatter.timeSince(version.createdAt)}
                            </time>
                        </div>
                        <div
                            className="tiptap argument-rich-content mt-2 w-full min-w-0 text-sm! leading-7"
                            dangerouslySetInnerHTML={{ __html: version.htmlContent ?? "" }}
                        />
                    </li>
                ))}
            </ol>
        </SimpleDialog>
    ));
}
